import { LRUCache } from 'lru-cache';
import murmurhash from 'murmurhash';
import { Serializable, Serializer } from './serializers';

export namespace Hasher {
  const defaultCache = new LRUCache<string, string>({
    max: 1000,
    ttl: 60 * 60 * 1000,
    allowStale: false,
  });

  export const hash = <T>(
    value: T,
    serializer?: Serializable<T>,
    cache?: LRUCache<string, string>
  ): string => {
    const c = cache ?? defaultCache;
    if (value === undefined) {
      return 'undefined';
    }
    if (value === null) {
      return 'null';
    }

    if (serializer) {
      const serialized = serializer.serialize(value);
      const hashed = murmurhash.v3(serialized).toString();
      if (c.has(serialized)) {
        return c.get(serialized)!;
      }
      c.set(serialized, hashed);
      return hashed;
    }

    // Find a serializer that can handle this value type
    const serializers = [
      Serializer.string,
      Serializer.domainDependencies,
      Serializer.frontendStartup,
      Serializer.domainSettings,
      Serializer.domainPreferences,
      Serializer.executorRole,
      Serializer.executorRoleList,
      Serializer.simulatedRole,
      Serializer.alias,
      Serializer.aliasList,
      Serializer.userRole,
      Serializer.timeSelection,
      Serializer.timeSliderState,
      Serializer.segmentation,
      Serializer.segmentationList,
      Serializer.assignedPermissions,
      Serializer.dashboard,
      Serializer.dashboardList,
      Serializer.dataFieldWithDataType,
      Serializer.dataFieldWithDataTypeList,
      Serializer.filters,
      Serializer.filterList,
      Serializer.allowedDomain,
      Serializer.allowedDomainList,
      Serializer.allUsedFields,
      Serializer.metrics,
      Serializer.metricTree,
      Serializer.firebaseUser,
      Serializer.frontendStartupUser,
      Serializer.performanceCycle,
      Serializer.performanceCycleList,
      Serializer.customSqlQueryValueBackendType,
      Serializer.language,
      Serializer.balanceSheetStoreConfig,
      Serializer.metricIdType,
      Serializer.metricIdTypeList,
    ];

    for (const s of serializers) {
      const typedSerializer = s as Serializable<unknown>;
      const canSerialize = typedSerializer.canSerialize(value);
      if (canSerialize) {
        const serialized = typedSerializer.serialize(value);
        const hashed = murmurhash.v3(serialized).toString();
        if (c.has(serialized)) {
          return c.get(serialized)!;
        }
        c.set(serialized, hashed);
        return hashed;
      }
    }

    throw new Error(
      `No serializer found for value of type ${typeof value} (${JSON.stringify(value)})`
    );
  };
}
